import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type PracticeItem = {
  practice: string;
  effect: string;
};

type Props = {
  type: 'best' | 'anti';
  title?: string;
  items: PracticeItem[];
};

/**
 * ベストプラクティス / アンチパターン を視覚的に表示するコンポーネント。
 * - best: 緑系。チェックアイコン付き。practice=「やるべきこと」、effect=「効果」
 * - anti: 赤系。×アイコン付き。practice=「やりがちなこと」、effect=「リスク」
 */
const WorkflowPracticeBox: React.FC<Props> = ({ type, title, items }) => {
  const isBest = type === 'best';
  const color = isBest ? '#2e7d32' : '#d32f2f';
  const label = title ?? (isBest ? 'ベストプラクティス' : 'アンチパターン');
  const effectLabel = isBest ? '効果' : 'リスク';

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          py: 1,
          bgcolor: `${color}0A`,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {isBest ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color }} />
        ) : (
          <HighlightOffIcon sx={{ fontSize: 18, color }} />
        )}
        <Chip
          label={label}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: `${color}18`,
            color,
            border: 'none',
          }}
        />
      </Stack>

      {/* アイテム一覧 */}
      <Stack spacing={0}>
        {items.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: idx < items.length - 1 ? 1 : 0,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            {/* 番号バッジ */}
            <Box
              sx={{
                minWidth: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: `${color}14`,
                color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
                mt: 0.1,
              }}
            >
              {idx + 1}
            </Box>
            {/* コンテンツ */}
            <Stack spacing={0.25} sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {item.practice}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Chip
                  label={effectLabel}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    bgcolor: `${color}10`,
                    color,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {item.effect}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default WorkflowPracticeBox;
